import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { E164PhoneNumber, getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { InboundSmsFieldsWithDefaults, InboundSmsSchemaTypes } from "../types/inboundSms.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "inboundSms" as const;

/**
 * One row per inbound SMS. `address`, `sentAt` and `body` deliberately match `notificationRecords` and
 * `outboundAiMessages` so a conversation can be merged across all three by sorting on `sentAt`.
 * Owner-scoped by number; never read from an org-facing surface.
 */
@index({ address: 1, sentAt: 1 })
@index({ messageSid: 1 }, { unique: true })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "inboundSms" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<InboundSms>>(...addSchemaKeyHook(SCHEMA_KEY))
export class InboundSms extends SchemaBase<InboundSms, InboundSmsFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** E.164 number the message came from. Same field name as `notificationRecords.address`. */
    @validate({ zodValidator: E164PhoneNumber })
    @prop({ type: () => String, required: true })
    public address!: string;

    /** When the grower sent it. Named to match `notificationRecords.sentAt` so a merged transcript
     * sorts on one field regardless of which collection a message came from. */
    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public sentAt!: Date;

    /** The exact text received. */
    @validate({ zodValidator: z.string() })
    @prop({ type: () => String, required: true })
    public body!: string;

    /** Twilio's message sid. Unique, so a webhook retry cannot log the same message twice. */
    @validate({ zodValidator: z.string() })
    @prop({ type: () => String, required: true })
    public messageSid!: string;

    /**
     * Whether the `X-Twilio-Signature` was verified. False when TWILIO_AUTH_TOKEN was absent, which
     * makes "which messages did we accept unverified" answerable from the data rather than from logs.
     */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public signatureVerified: boolean = false;

    /**
     * The descope user this number resolved to, written by the worker once resolution succeeds. Absent
     * means the number never resolved to an identity, so a row without it is an inbound message that
     * was accepted but could not be acted on.
     */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public resolvedDescopeIdentity?: string;

    static getters = {} as const satisfies Record<string, (self: InboundSms) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<InboundSmsSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<InboundSms, InboundSmsFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

InboundSms.dtoValidators = getZodSchemaFromClass(InboundSms) as unknown as DtoValidatorTypes<InboundSmsSchemaTypes>;
